import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

function useSwipe(onPrev, onNext, enabled) {
  const start = useRef(null);
  const onTouchStart = (e) => { if (enabled && e.touches.length === 1) start.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (start.current === null) return;
    const dx = e.changedTouches[0].clientX - start.current;
    start.current = null;
    if (Math.abs(dx) > 50) (dx < 0 ? onNext : onPrev)();
  };
  return { onTouchStart, onTouchEnd };
}

function useZoom() {
  const [scale, setScale] = useState(1);
  const pinch = useRef(null);
  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const onTouchStart = (e) => { if (e.touches.length === 2) pinch.current = { d: dist(e.touches), s: scale }; };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinch.current) { e.preventDefault(); setScale(Math.min(4, Math.max(1, pinch.current.s * dist(e.touches) / pinch.current.d))); }
  };
  const onTouchEnd = () => { pinch.current = null; };
  const toggle = () => setScale((s) => (s > 1 ? 1 : 2.5));
  return { scale, reset: () => setScale(1), toggle, handlers: { onTouchStart, onTouchMove, onTouchEnd } };
}

function useLightboxKeys(open, onClose, prev, next) {
  useEffect(() => {
    if (!open) return undefined;
    const keys = { Escape: onClose, ArrowLeft: prev, ArrowRight: next };
    const onKey = (e) => keys[e.key]?.();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose, prev, next]);
}

function NavArrows({ prev, next }) {
  const cls = "absolute top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 sm:flex";
  return (
    <>
      <button onClick={prev} className={`${cls} left-2`} aria-label="Previous" data-testid="lightbox-prev"><ChevronLeft /></button>
      <button onClick={next} className={`${cls} right-2`} aria-label="Next" data-testid="lightbox-next"><ChevronRight /></button>
    </>
  );
}

function Caption({ g }) {
  if (!g.caption && !g.event_title) return null;
  return <p className="px-4 pb-6 pt-3 text-center text-sm" data-testid="lightbox-caption"><span className="font-semibold text-gold">{g.event_title}</span>{g.event_title && g.caption ? " — " : ""}{g.caption}</p>;
}

export function Lightbox({ items, index, onClose, onIndex, src = (u) => u }) {
  const open = index !== null && index >= 0 && items[index];
  const zoom = useZoom();
  const prev = useCallback(() => { zoom.reset(); onIndex((index - 1 + items.length) % items.length); }, [index, items.length, onIndex, zoom]);
  const next = useCallback(() => { zoom.reset(); onIndex((index + 1) % items.length); }, [index, items.length, onIndex, zoom]);
  const swipe = useSwipe(prev, next, zoom.scale === 1);
  useLightboxKeys(open, onClose, prev, next);
  if (!open) return null;
  const g = items[index];
  return createPortal(
    <div className="fixed inset-0 z-[80] flex flex-col bg-black/95 text-parchment animate-in fade-in" role="dialog" aria-modal="true" data-testid="lightbox">
      <div className="flex items-center justify-between px-4 py-3">
        <span className="font-mono-accent text-xs" data-testid="lightbox-counter">{index + 1} / {items.length}</span>
        <button onClick={onClose} className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10" aria-label="Close" data-testid="lightbox-close"><X size={22} /></button>
      </div>
      <div className="relative flex flex-1 touch-none items-center justify-center overflow-hidden"
        onTouchStart={(e) => { swipe.onTouchStart(e); zoom.handlers.onTouchStart(e); }} onTouchMove={zoom.handlers.onTouchMove}
        onTouchEnd={(e) => { zoom.handlers.onTouchEnd(e); swipe.onTouchEnd(e); }} onDoubleClick={zoom.toggle}>
        <img src={src(g.url)} alt={g.caption || g.album || ""} style={{ transform: `scale(${zoom.scale})`, transition: "transform 80ms" }}
          className="max-h-full max-w-full select-none object-contain" draggable={false} data-testid="lightbox-image" />
        {items.length > 1 && <NavArrows prev={prev} next={next} />}
      </div>
      <Caption g={g} />
      <p className="pb-[max(env(safe-area-inset-bottom),12px)] text-center text-[11px] text-parchment/50 sm:hidden">Swipe to browse · pinch or double-tap to zoom</p>
    </div>, document.body);
}
